import styled from "styled-components";
import { useSelector } from "react-redux";
import { useState, Fragment, useRef, useEffect, Suspense, useCallback } from "react";
import ABPlayer from "components/elements/ABPlayer";
import { getDrawingBox } from "reduxModules/ducks/ui";
import { getArea, getCurrentValue2 } from "reduxModules/ducks/container";
import { closest } from "utilities/mathematic";
import { isInside } from "utilities/geometric";
import { capitalize, has, isEmpty, keys, minBy, omit, values } from "lodash";
import audioFileLinks from "globalConstants/AudioLinks";
import toolIndex from "toolConstants";


/**
 * Creates a resource object for React Suspense that wraps an asynchronous operation
 * 
 * @function
 * @param {Function} asyncFn - Asynchronous function that returns a Promise
 * @returns {Object} Resource object with read method that works with React Suspense
 * @returns {Function} Resource.read - Method that returns resolved value or throws Promise/error
 */
const createResource = (asyncFn) => {
  let status = "pending";
  let result;
  const promise = asyncFn()
    .then(
      resolved => {
        status = "success";
        result = resolved;
      },
      error => {
        status = "error";
        result = error;
      }
    );

  return {
    read() {
      if (status === "pending") throw promise;
      if (status === "error") throw result;
      return result;
    }
  };
};



/**
 * Universal audio content component that handles both single and A/B sources
 * Used with React Suspense to handle async loading states
 * 
 * @component
 * @param {Object} props - The component props
 * @param {Object} props.resource - Resource object with read method
 * @param {React.RefObject} props.audioRef - Reference to the audio element
 * @param {Function} [props.onError] - Optional callback for handling errors
 * @returns {React.ReactElement|null} - Audio element or A/B player
 */
const AudioContent = ({ resource, audioRef, onError }) => {
  const isMountedRef = useRef(true);

  // Set isMounted to false on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  try {
    // Read the resource - could be a single URL or {acousticURL, rawURL} object
    const sourceData = resource.read();

    // Handle different source data formats
    if (!sourceData) return null;

    if (typeof sourceData === 'string') {
      // Single URL case (CRD)
      return (
        <AudioPlayer.Container expand={false}>
          <audio ref={audioRef} controls={true} src={sourceData}>
            Error: Your browser does not support HTML5 audio.
          </audio>
        </AudioPlayer.Container>
      );
    } else if (sourceData.acousticURL) {
      // Dual URL case (WD)
      if (sourceData.rawURL) {
        return (
          <AudioPlayer.Container expand={true}>
            <ABPlayer
              sourceA={sourceData.acousticURL}
              sourceB={sourceData.rawURL}
            />
          </AudioPlayer.Container>
        );
      } else {
        // Fallback to single URL if rawURL is missing
        return (
          <AudioPlayer.Container expand={false}>
            <audio ref={audioRef} controls={true} src={sourceData.acousticURL}>
              Error: Your browser does not support HTML5 audio.
            </audio>
          </AudioPlayer.Container>
        );
      }
    }

    return null;
  } catch (error) {
    if (error instanceof Promise) {
      throw error; // Let Suspense handle this
    }

    console.error("Audio error:", error);
    if (onError) {
      onError(error);
    }
    return null;
  }
};


/**
 * Loading indicator for audio content during async loading
 * 
 * @component
 * @returns {React.ReactElement} - Loading message in styled container
 */
const LoadingBubble = () => (
  <AudioPlayer.Container expand={false}>
    <div>Generating audio simulation...</div>
  </AudioPlayer.Container>
);


/**
 * Error display component for audio loading failures
 * 
 * @component
 * @param {Object} props - The component props
 * @param {string|Error} [props.error] - Error message or object to display
 * @returns {React.ReactElement} - Error message in styled container
 */
const ErrorBubble = ({ error }) => (
  <AudioPlayer.Container expand={false}>
    <div style={{ color: 'red' }}>
      {error || "Error loading audio"}
    </div>
  </AudioPlayer.Container>
);


/**
 * Conference Room Designer Audio Player component
 * Renders an audio element that simulates how sound would be perceived at a specific location
 * in a conference room design, based on microphone placement and room acoustics
 * 
 * @component
 * @param {Object} props - The component props
 * @param {Object} props.modelCoords - Coordinates in model space where audio should be sampled
 * @param {number} props.modelCoords.x - X coordinate in model space
 * @param {number} props.modelCoords.y - Y coordinate in model space
 * @returns {React.ReactElement|null} - Audio player element or null if no valid source
 */
const CRDAudioPlayer = ({ modelCoords }) => {
  const [resource, setResource] = useState(null);
  const audioRef = useRef(null);
  const [error, setError] = useState(null);

  // Get necessary state from Redux
  const microphones = useSelector(state => getCurrentValue2(state, 'microphones.layout'));
  const acoustics = useSelector(state => getCurrentValue2(state, 'acoustics'));

  // Get audio source URL based on position
  const fetchAudioSource = useCallback(async () => {
    setError(null);
    await new Promise(resolve => setTimeout(resolve, 10));

    const { x, y } = modelCoords;

    // Find closest microphone and calculate distance
    const closestDevice = isEmpty(microphones)
      ? null
      : minBy(microphones, device => Math.hypot(device.location.x - x, device.location.y - y));

    if (!closestDevice) {
      setError("No microphones found in the room");
      return null;
    }

    const distance = Math.hypot(closestDevice.location.x - x, closestDevice.location.y - y);

    // Determine audio file based on acoustics and distance
    const linkId = `${capitalize(acoustics)}-${closest([-100, 1, 2, 3, 4, 5, 7.5, 10], distance)}M.wav`;

    if (!has(audioFileLinks, linkId)) {
      setError(`No audio sample available for ${acoustics} acoustics at ${distance.toFixed(1)}m`);
      return null;
    }

    // Return the audio source URL
    return `https://support.biamp.com/@api/deki/files/${audioFileLinks[linkId]}/${linkId}`;
  }, [modelCoords, microphones, acoustics]);

  // Set audio source when position changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.pause();
    };
    setResource(createResource(fetchAudioSource));
  }, [modelCoords, microphones, acoustics, fetchAudioSource]);

  // Reset audio element when unmounting
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);

  // Don't render if no valid source
  if (!resource) return null;

  return error ? <ErrorBubble error={error} /> : <AudioContent resource={resource} audioRef={audioRef} onError={setError}/>;
};


/**
 * Warehouse Designer Audio Player component
 * Fetches and renders specialized audio samples for warehouse environments
 * by making API calls to a simulation service
 * 
 * @component
 * @param {Object} props - The component props
 * @param {Object} props.modelCoords - Coordinates in model space where audio should be sampled
 * @param {number} props.modelCoords.x - X coordinate in model space
 * @param {number} props.modelCoords.y - Y coordinate in model space
 * @returns {React.ReactElement|null} - Audio content or error message
 */
const WDAudioPlayer = ({ modelCoords }) => {
  const audioRef = useRef(null);
  const [resource, setResource] = useState(null);
  const [error, setError] = useState(null);
  const [isCleaningUp, setIsCleaningUp] = useState(false);

  // Get necessary state from Redux
  const currentContainer = useSelector(state => state.container);
  const currentTool = useSelector(state => state.container.currentTool);

  // Function to stop any currently playing audio
  const stopCurrentAudio = () => {
    // Find all audio elements in the document
    const allAudioElements = document.querySelectorAll('audio');

    // Pause each one
    allAudioElements.forEach(audio => {
      if (audio && !audio.paused) {
        audio.pause();
      }
    });
  };

  // Create fetch resource when coordinates change
  useEffect(() => {

    setIsCleaningUp(true);
    stopCurrentAudio();

    const fetchAuralAudio = async () => {
      try {

        // Wait a short moment to ensure cleanup has completed
        await new Promise(resolve => setTimeout(resolve, 10));
        setIsCleaningUp(false);

        const auralLocation = {
          left: modelCoords.x,
          top: modelCoords.y
        };

        const { container: containerFilterKeys, area: areaFilter } = toolIndex[currentTool].constants.callFilter;
        const areaFilterKeys = areaFilter(true);
        const { areas, ...container } = omit(currentContainer, containerFilterKeys);

        const filtered = keys(areas).reduce((acc, key) => {
          acc[key] = omit(areas[key], areaFilterKeys);
          return acc;
        }, {});

        const payload = { auralLocation, ...container, ['areas']: filtered };
        const url = "https://wdakustxengine-20250605v2p0.azurewebsites.net//api/GetAuralOfSpeakerLayout";

        const response = await fetch(url, {
          "method": "POST",
          "headers": {
            "content-type": "text/plain",
            "accept": "*/*"
          },
          "body": JSON.stringify(payload),
        });

        if (!response.ok) {
          setError(`Server responded with status: ${response.status}`);
        }

        const body = await response.text();
        const data = JSON.parse(body);

        if ('errornumber' in data) {
          setError(data.errortext.split('\n')[0]);
          console.log("Error: ", data.errortext);
        };

        return {
          rawURL: data.URLDirectOnly, //'https://support.biamp.com/@api/deki/files/19525/Auralization_audio.mp3', // data.URL, 
          acousticURL: data.URL
        };

      } catch (err) {
        console.error("Error fetching warehouse audio:", err);
        setError(err);
        setIsCleaningUp(false);
      };
    };

    // Create the resource
    setResource(createResource(fetchAuralAudio));

    // Cleanup audio on unmount or coordinates change
    return () => {
      setIsCleaningUp(true);
      stopCurrentAudio();

      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, [modelCoords, currentContainer, currentTool]);

  // If no resource, don't render anything
  // If we're in cleanup or don't have a resource, don't render anything
  if (isCleaningUp || !resource) return null;

  // Render the audio content with the resource
  return error ? <ErrorBubble error={error} /> : <AudioContent resource={resource} audioRef={audioRef} onError={setError} />;
};


/**
 * Main AudioPlayer component that determines position and renders the appropriate
 * audio player type based on the current tool and mouse position
 * 
 * @component
 * @param {Object} props - The component props
 * @param {Object} props.anchor - Screen coordinates where the player should be anchored
 * @param {number} props.anchor.x - X screen coordinate
 * @param {number} props.anchor.y - Y screen coordinate
 * @param {boolean} props.mouseActive - Whether mouse interaction is active
 * @returns {React.ReactElement|null} - Audio player or null if no valid target
 */
const AudioPlayer = (props) => {
  const { anchor, mouseActive } = props;
  const { bbox: { x0, y0 }, scale, zoom } = useSelector(getDrawingBox);
  const currentTool = useSelector(state => state.container.currentTool);
  const [target, setTarget] = useState(null);

  // Calculates the mouse position relative to the drawing area
  const x = (anchor.x - x0) / scale;
  const y = (anchor.y - y0) / scale;

  // Selects the current area based on mouse position
  const inArea = useSelector(getArea);
  const { id, location, speakerLayout } = useSelector(state => values(state.container.areas).filter(area => {
    return isInside([x + (zoom ? area.location.left : 0), y + (zoom ? area.location.top : 0)], { ...area.location, ...area.dimensions }) && (!inArea || inArea === area.id)
  }).pop() ?? {});
  // For WD check if mouse is inside an area of coverage
  const inPlot = speakerLayout?.some(map => isInside([x + (zoom ? location.left : 0), y + (zoom ? location.top : 0)], { ...map.bbox }));

  // Clear target on mount
  useEffect(() => {
    setTarget(null);
  }, []);

  // Clear target on zoom change
  useEffect(() => {
    setTarget(null);
  }, [zoom]);

  // Set the target
  useEffect(() => {
    if (mouseActive & !isEmpty(anchor)) {
      setTarget(
        id && !(currentTool === 'warehouseDesigner' && !inPlot)
          ? { x: x + (zoom ? location.left : 0), y: y + (zoom ? location.top : 0) }
          : null
      );
    };
    // eslint-disable-next-line 
  }, [id, inPlot, anchor]);

  // Add a document-level click handler to detect clicks outside
  useEffect(() => {
    // Only add the listener if we have an active audio player
    if (target !== null) {
      const handleOutsideClick = (event) => {
        // Check if click is outside of the audio player
        const audioWrapper = document.querySelector('.AudioPlayer-wrapper');
        if (audioWrapper && !audioWrapper.contains(event.target)) {
          // Check if click is outside of MouseHandler
          const mouseHandler = document.querySelector('.MouseHandler');
          if (!mouseHandler || !mouseHandler.contains(event.target)) {
            // Click is outside both audio player and MouseHandler
            setTarget(null);
          }
        }
      };

      document.addEventListener('click', handleOutsideClick);
      return () => {
        document.removeEventListener('click', handleOutsideClick);
      };
    }
  }, [target]);

  // Add a ref to store the timeout ID
  const timeoutRef = useRef(null);

  // Set up timeout to dismiss player after 2 minutes
  useEffect(() => {
    // Clear any existing timeout when target changes
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // Set a new timeout if we have a valid target
    // if (target !== null) {
    //   timeoutRef.current = setTimeout(() => {
    //     console.log("Audio player automatically dismissed after timeout");
    //     setTarget(null);
    //   }, 120000); // 120000 ms = 2 minute
    // }

    // Clean up on unmount
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [target]);

  // Don't render anything if no anchor or not active
  if (target === null) return null;

  return (
    <Fragment>
      {<AudioPlayer.Wrapper {...anchor} expand={currentTool === 'warehouseDesigner'}>
        <Suspense fallback={<LoadingBubble />}>
          {currentTool === 'warehouseDesigner'
            ? <WDAudioPlayer modelCoords={target} />
            : <CRDAudioPlayer modelCoords={target} />
          }
        </Suspense>
      </AudioPlayer.Wrapper>}
    </Fragment>
  )
};

/**
 * Styled component that wraps the audio player and positions it on screen
 * 
 * @component
 * @type {import('styled-components').StyledComponent}
 */
AudioPlayer.Wrapper = styled.foreignObject.attrs(props => ({
  className: 'AudioPlayer-wrapper',
  width: '304',
  height: props.expand ? '120' : '70',
 // '120', // 
  transform: 'translate(-40, 0)'
}))`
  pointer-events: auto !important;
`;

/**
 * Styled component for the audio player container with speech bubble styling
 * 
 * @component
 * @type {import('styled-components').StyledComponent}
 */
AudioPlayer.Container = styled.div.attrs(props => ({
  className: 'd-inline-flex align-items-center justify-content-center audioPlayer-container',
}))`
  align-items: center;
  width: 304px;
  height: ${props => props.expand ? '100px' : '58px'}; 
  ${'' /* was 58px */}
  position: relative;
  margin-top: 10px;
  border: 2px solid #000;
  border-radius: 20px; 
  background-color: #f2f2f2;
  font-size: 14px;
  text-align: center;
  pointer-events: auto !important;
  &::after {
    content: "";
    position: absolute;
    top: -24px;
    left: 28px;
    border-width: 12px;
    border-style: solid;
    border-color: transparent transparent #000 transparent;
  }
`

export default AudioPlayer